import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowDownward,
  ArrowUpward,
  ChevronRight,
  CreateNewFolder as CreateNewFolderIcon,
  Delete as DeleteIcon,
  Description as FileIcon,
  Download as DownloadIcon,
  DriveFileRenameOutline as RenameIcon,
  DriveFileMove as MoveIcon,
  Folder as FolderIcon,
  Folder as FilledFolderIcon,
  InsertLink as LinkIcon,
  ContentCopy as CopyIcon,
  Link as AccessLinkIcon,
  Menu as MenuIcon,
  People as RolesIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  ViewList as ViewListIcon,
  GridView as GridViewIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  useGetFolderItemsQuery,
  useGetTrashItemsQuery,
  useDownloadFileMutation,
  useRenameItemMutation,
  useCreateDirectoryMutation,
  useRemoveItemMutation,
  useMoveItemMutation,
  useCopyItemMutation,
  useCreateLinkFileMutation,
  useChangeRoleMutation,
  type FileStorageItem,
} from './api/fileStorageApi';
import { normalizeUrl } from './utils/url';
import {
  downloadFromResponse,
  formatFileSize,
  sortFileItems,
  type FileSortKey,
} from './utils/fileStorageHelpers';
import { extractErrorMessage } from './utils/fileStorageErrors';
import { getPathFromBreadcrumb } from './utils/fileStorageBreadcrumb';
import { FolderTreeNode } from './components/file-manager/FolderTreeNode';
import {
  AccessLinkDialog,
  RolesDialog,
  ConfirmDialog,
  CreateFolderDialog,
  CreateLinkDialog,
  RenameDialog,
  TransferItemsDialog,
} from './components/file-manager';
import { UploadProgressPanel } from './components/UploadProgressPanel';
import { FileStoragePathHeader } from './components/common/FileStoragePathHeader';
import { useFileUploads } from './hooks/useFileUploads';
type PathItem = { id: string | undefined; name: string };
type DisplayMode = 'list' | 'tiles';
type DeleteDialogPhase = 'idle' | 'processing' | 'error';

const ROOT: PathItem = { id: undefined, name: 'Files' };

const itemIcon = (item: FileStorageItem) => {
  if (item.isFolder) return <FolderIcon fontSize="small" color="warning" />;
  if (item.isUrl) return <LinkIcon fontSize="small" color="info" />;
  return <FileIcon fontSize="small" color="action" />;
};

const formatRoles = (roles: string[]): string => {
  if (!roles.length) return '—';
  return roles.join(', ');
};

// ── Main FileManagerPage ──────────────────────────────────────────────────────

export const FileManagerPage = ({ title }: { title?: string }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isNavCollapsed, setIsNavCollapsed] = useState(isMobile);
  const [isFilesNavExpanded, setIsFilesNavExpanded] = useState(true);
  const [viewMode, setViewMode] = useState<'files' | 'trash'>('files');
  const [path, setPath] = useState<PathItem[]>([ROOT]);
  const currentFolderId = path[path.length - 1].id;

  const [sortKey, setSortKey] = useState<FileSortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('list');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mobileRolesTooltipId, setMobileRolesTooltipId] = useState<string | null>(null);

  const [newLinkOpen, setNewLinkOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [initialSelectedRoles, setInitialSelectedRoles] = useState<string[]>([]);
  const [accessLinkFileId, setAccessLinkFileId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<FileStorageItem | null>(null);
  const [deleteTargets, setDeleteTargets] = useState<FileStorageItem[]>([]);
  const [deleteDialogPhase, setDeleteDialogPhase] = useState<DeleteDialogPhase>('idle');
  const [deleteDialogError, setDeleteDialogError] = useState<string | null>(null);
  const [transferMode, setTransferMode] = useState<'move' | 'copy' | null>(null);
  const [transferTargets, setTransferTargets] = useState<FileStorageItem[]>([]);

  const [errorSnackbar, setErrorSnackbar] = useState<string | null>(null);
  const [uploadPanelOpen, setUploadPanelOpen] = useState(false);

  const uploadRef = useRef<HTMLInputElement | null>(null);
  const lastNonEmptySelectedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (selectedIds.size > 0) {
      lastNonEmptySelectedIdsRef.current = new Set(selectedIds);
    }
  }, [selectedIds]);

  const {
    data: folderData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetFolderItemsQuery(
    { nodeKey: currentFolderId, storageType: 'All files' },
    { skip: viewMode === 'trash' }
  );
  const { data: rootFolderData } = useGetFolderItemsQuery(
    { nodeKey: undefined, storageType: 'All files' },
    { skip: false }
  );

  const {
    data: trashData,
    isLoading: isTrashLoading,
    isFetching: isTrashFetching,
    error: trashError,
    refetch: refetchTrash,
  } = useGetTrashItemsQuery(undefined, { skip: viewMode !== 'trash' });

  const {
    entries: uploadEntries,
    uploadFiles,
    cancelUpload,
    retryUpload,
  } = useFileUploads({
    onComplete: () => {
      void refetch();
    },
    onError: setErrorSnackbar,
  });

  const [downloadFile, { isLoading: isDownloading }] = useDownloadFileMutation();
  const [renameItem, { isLoading: isRenaming }] = useRenameItemMutation();
  const [createDirectory, { isLoading: isCreatingDir }] = useCreateDirectoryMutation();
  const [removeItem, { isLoading: isDeleting }] = useRemoveItemMutation();
  const [moveItem, { isLoading: isMoving }] = useMoveItemMutation();
  const [copyItem, { isLoading: isCopying }] = useCopyItemMutation();
  const [createLinkFile, { isLoading: isCreatingLink }] = useCreateLinkFileMutation();
  const [changeRole, { isLoading: isChangingRole }] = useChangeRoleMutation();

  const busy =
    isLoading ||
    isFetching ||
    isTrashLoading ||
    isTrashFetching ||
    isDownloading ||
    isRenaming ||
    isCreatingDir ||
    isDeleting ||
    isMoving ||
    isCopying ||
    isCreatingLink ||
    isChangingRole;

  const activeData = viewMode === 'trash' ? trashData : folderData;
  const activeError = viewMode === 'trash' ? trashError : error;
  const items = useMemo(() => activeData?.fileItems ?? [], [activeData?.fileItems]);

  const rootItems = useMemo(() => rootFolderData?.fileItems ?? [], [rootFolderData?.fileItems]);

  const sortedItems = useMemo(() => {
    return sortFileItems(items, sortKey, sortDir);
  }, [items, sortKey, sortDir]);

  const handleSort = (key: FileSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  };

  const sortIcon = (key: FileSortKey) =>
    sortKey === key ? (
      sortDir === 'asc' ? (
        <ArrowUpward fontSize="inherit" />
      ) : (
        <ArrowDownward fontSize="inherit" />
      )
    ) : null;

  const navigate = (p: PathItem[]) => {
    if (viewMode === 'trash') return;
    setPath(p);
    setSelectedIds(new Set());
  };

  const handleOpenBreadcrumb = (segmentIndex: number) => {
    const nextPath = getPathFromBreadcrumb(path, segmentIndex, 1);
    if (!nextPath) return;
    navigate(nextPath);
  };

  const breadcrumbSegments = useMemo(
    () =>
      path.slice(1).map((item, index) => ({
        id: item.id ?? `path-segment-${index}`,
        name: item.name,
      })),
    [path]
  );

  const switchMode = (mode: 'files' | 'trash') => {
    setViewMode(mode);
    setSelectedIds(new Set());
    if (mode === 'trash') {
      setIsNavCollapsed(true);
    }
    if (mode === 'files') {
      setPath([ROOT]);
    }
  };

  const buildItemPathInfo = (item: FileStorageItem) => {
    if (viewMode === 'trash') {
      return [
        { key: null, name: 'trash' },
        { key: item.id, name: item.name },
      ];
    }

    const basePath = path
      .slice(1)
      .map((p) => ({ key: p.id ?? null, name: p.name }))
      .filter((p): p is { key: string; name: string } => !!p.key);

    return [...basePath, { key: item.id, name: item.name }];
  };

  const buildPathInfoFromPath = (targetPath: PathItem[]) =>
    targetPath
      .slice(1)
      .map((p) => ({ key: p.id ?? null, name: p.name }))
      .filter((p): p is { key: string; name: string } => !!p.key);

  const runOperation = async (
    action: () => Promise<void>,
    fallbackErrorMessage: string,
    options?: {
      onFinally?: () => void;
    }
  ) => {
    try {
      await action();
    } catch (err) {
      setErrorSnackbar(extractErrorMessage(err, fallbackErrorMessage));
    } finally {
      options?.onFinally?.();
    }
  };

  const handleOpenItem = async (item: FileStorageItem) => {
    if (item.isFolder) {
      navigate([...path, { id: item.id, name: item.name }]);
      return;
    }
    if (item.isUrl && item.link) {
      window.open(normalizeUrl(item.link), '_blank', 'noopener,noreferrer');
      return;
    }
    if (!item.id) return;

    await runOperation(async () => {
      const res = await downloadFile({ key: item.id }).unwrap();
      if (!downloadFromResponse(res, item.name)) {
        throw new Error(
          'Could not decode the file. The download data may be invalid or corrupted.'
        );
      }
    }, 'Failed to download file.');
  };

  const handleRename = (item: FileStorageItem) => setRenameTarget(item);

  const handleRenameSubmit = async (newName: string) => {
    if (!renameTarget) return;
    await runOperation(async () => {
      await renameItem({ id: renameTarget.id, newName }).unwrap();
      setRenameTarget(null);
    }, 'Failed to rename item.');
  };

  const handleDeleteSelected = () => {
    const ids = selectedIds.size > 0 ? selectedIds : lastNonEmptySelectedIdsRef.current;
    setDeleteTargets(sortedItems.filter((i) => ids.has(i.id)));
    setDeleteDialogPhase('idle');
    setDeleteDialogError(null);
  };

  const handleDeleteConfirm = async () => {
    if (deleteTargets.length === 0) return;
    if (deleteDialogPhase === 'processing') return;

    setDeleteDialogPhase('processing');
    setDeleteDialogError(null);

    try {
      await Promise.all(
        deleteTargets.map((item) =>
          removeItem({
            pathInfo: buildItemPathInfo(item),
            isDirectory: item.isFolder,
          }).unwrap()
        )
      );
      if (deleteTargets.length === 1) {
        const [item] = deleteTargets;
        setSelectedIds((prev) => {
          const s = new Set(prev);
          s.delete(item.id);
          return s;
        });
      } else {
        setSelectedIds(new Set());
      }
      setDeleteTargets([]);
      setDeleteDialogPhase('idle');
      setDeleteDialogError(null);
    } catch (err) {
      const fallbackMessage =
        viewMode === 'trash'
          ? 'Failed to permanently delete selected item(s).'
          : 'Failed to delete selected item(s).';
      const message = extractErrorMessage(err, fallbackMessage);
      setDeleteDialogPhase('error');
      setDeleteDialogError(message);
      setErrorSnackbar(message);
    }
  };

  const handleCreateFolder = async (name: string) => {
    await runOperation(async () => {
      await createDirectory({ parentId: currentFolderId, name }).unwrap();
      setCreateFolderOpen(false);
    }, 'Failed to create folder.');
  };

  const handleDeleteClose = () => {
    if (deleteDialogPhase === 'processing') return;
    setDeleteTargets([]);
    setDeleteDialogPhase('idle');
    setDeleteDialogError(null);
  };

  const openTransferDialog = (mode: 'move' | 'copy', itemsToTransfer: FileStorageItem[]) => {
    if (itemsToTransfer.length === 0) return;
    setTransferTargets(itemsToTransfer);
    setTransferMode(mode);
  };

  const handleTransferClose = () => {
    setTransferMode(null);
    setTransferTargets([]);
  };

  const handleTransferConfirm = async (destinationPath: PathItem[]) => {
    if (!transferMode || transferTargets.length === 0) return;
    const destinationPathInfo = buildPathInfoFromPath(destinationPath);
    const action = transferMode === 'move' ? moveItem : copyItem;

    await runOperation(async () => {
      await Promise.all(
        transferTargets.map((item) =>
          action({
            sourcePathInfo: buildItemPathInfo(item),
            sourceIsDirectory: item.isFolder,
            destinationPathInfo,
          }).unwrap()
        )
      );
      navigate(destinationPath);
      handleTransferClose();
    }, `Failed to ${transferMode} selected item(s).`);
  };

  const deleteDialogText = useMemo(() => {
    if (deleteTargets.length === 0) {
      return { title: '', description: '', confirmLabel: '' };
    }
    if (viewMode === 'trash' && deleteDialogPhase === 'processing') {
      return {
        title: 'Deleting from trash',
        description: 'Processing removal from trash. Please wait...',
        confirmLabel: 'Deleting...',
      };
    }
    if (viewMode === 'trash' && deleteDialogPhase === 'error') {
      return {
        title: 'Delete permanently',
        description:
          'We could not complete the removal from trash. Review the error and try again.',
        confirmLabel: 'Try again',
      };
    }
    if (deleteTargets.length === 1) {
      const name = deleteTargets[0].name;
      return viewMode === 'trash'
        ? {
            title: 'Delete permanently',
            description: `"${name}" will be permanently deleted.`,
            confirmLabel: 'Delete permanently',
          }
        : {
            title: 'Move to trash',
            description: `"${name}" will be moved to trash.`,
            confirmLabel: 'Move to trash',
          };
    }

    return viewMode === 'trash'
      ? {
          title: 'Delete permanently',
          description: `${deleteTargets.length} items will be permanently deleted.`,
          confirmLabel: 'Delete permanently',
        }
      : {
          title: 'Move to trash',
          description: `${deleteTargets.length} items will be moved to trash.`,
          confirmLabel: 'Move to trash',
        };
  }, [deleteDialogPhase, deleteTargets, viewMode]);

  const handleNewLink = async (name: string, url: string) => {
    const pathKeys = path.map((item) => item.id).filter((id): id is string => Boolean(id));
    const currentPath = path
      .map((item) => item.name)
      .filter(Boolean)
      .join('/');

    await runOperation(async () => {
      await createLinkFile({
        parentId: currentFolderId,
        path: currentPath,
        pathKeys,
        urlName: name,
        url,
      }).unwrap();
      setNewLinkOpen(false);
    }, 'Failed to create link.');
  };

  const handleUploadChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!files.length) return;

    setUploadPanelOpen(true);
    void uploadFiles(
      files,
      currentFolderId,
      path
        .slice(1)
        .map((item) => ({ key: item.id ?? '', name: item.name }))
        .filter((item) => item.key)
    );
  };

  const handleRolesSubmit = async (roles: string[]) => {
    await runOperation(async () => {
      await changeRole({ roles, filesKeys: [...selectedIds] }).unwrap();
      setRolesOpen(false);
      setInitialSelectedRoles([]);
      setSelectedIds(new Set());
    }, 'Failed to update roles.');
  };

  const calculateSharedRoles = (fileKeys: string[]): string[] => {
    const selectedItems = items.filter((item) => fileKeys.includes(item.id));
    if (!selectedItems.length) return [];

    const [firstItem, ...restItems] = selectedItems;
    return firstItem.roles.filter((role) => restItems.every((item) => item.roles.includes(role)));
  };

  const openRolesDialog = (fileKeys: string[]) => {
    setSelectedIds(new Set(fileKeys));
    setInitialSelectedRoles(calculateSharedRoles(fileKeys));
    setRolesOpen(true);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const s = new Set(prev);

      if (s.has(id)) {
        s.delete(id);
      } else {
        s.add(id);
      }

      return s;
    });

  const handleRolesClick = (id: string) => {
    if (!isMobile) return;
    setMobileRolesTooltipId((prev) => (prev === id ? null : id));
  };
  const handleScreenTap = () => {
    if (!isMobile || mobileRolesTooltipId === null) return;
    setMobileRolesTooltipId(null);
  };

  const allSelected = sortedItems.length > 0 && sortedItems.every((i) => selectedIds.has(i.id));
  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(sortedItems.map((i) => i.id)));

  const selectionToolbarIds =
    selectedIds.size > 0 ? selectedIds : lastNonEmptySelectedIdsRef.current;
  const toolbarSelectedItems = useMemo(() => {
    const ids = selectedIds.size > 0 ? selectedIds : lastNonEmptySelectedIdsRef.current;
    return sortedItems.filter((item) => ids.has(item.id));
  }, [sortedItems, selectedIds]);
  const singleToolbarItem = toolbarSelectedItems.length === 1 ? toolbarSelectedItems[0] : undefined;
  const canToolbarDownload =
    viewMode === 'files' &&
    singleToolbarItem &&
    !singleToolbarItem.isFolder &&
    !singleToolbarItem.isUrl;
  const canToolbarRename = viewMode === 'files' && Boolean(singleToolbarItem);
  const canToolbarAccessLink =
    viewMode === 'files' && singleToolbarItem && !singleToolbarItem.isFolder;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box
      onClick={handleScreenTap}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Breadcrumbs */}
      <Box
        sx={{
          px: { xs: 1.25, md: 2 },
          pt: 1,
          pb: 0.5,
          minHeight: 34,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <FileStoragePathHeader
          rootLabel={viewMode === 'trash' ? 'Trash' : title || ROOT.name}
          segments={viewMode === 'trash' ? [] : breadcrumbSegments}
          onRootClick={() => navigate([ROOT])}
          onSegmentClick={handleOpenBreadcrumb}
        />
      </Box>

      {/* Toolbar */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        sx={{
          mx: { xs: 1.25, md: 2 },
          mt: 0.5,
          mb: 0,
          px: 1,
          py: 0.75,
          alignItems: { xs: 'stretch', md: 'center' },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          transition: 'border-color 180ms ease, box-shadow 180ms ease',
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Tooltip title={isNavCollapsed ? 'Show navigation' : 'Hide navigation'}>
            <IconButton
              size="small"
              onClick={() => setIsNavCollapsed((v) => !v)}
              sx={{
                transition: 'transform 220ms ease, background-color 180ms ease',
                transform: isNavCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
          <Button
            size="small"
            variant={viewMode === 'files' ? 'contained' : 'text'}
            onClick={() => switchMode('files')}
          >
            Files
          </Button>
          <Button
            size="small"
            color={viewMode === 'trash' ? 'error' : 'inherit'}
            variant={viewMode === 'trash' ? 'contained' : 'text'}
            startIcon={<DeleteIcon />}
            onClick={() => switchMode('trash')}
          >
            Trash
          </Button>
        </Stack>

        <Box
          sx={{
            width: { xs: '100%', md: '1px' },
            height: { xs: '1px', md: 24 },
            bgcolor: 'divider',
          }}
        />

        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
          <Button
            size="small"
            startIcon={<CreateNewFolderIcon />}
            onClick={() => setCreateFolderOpen(true)}
            disabled={viewMode === 'trash'}
          >
            New folder
          </Button>
          <Button
            size="small"
            startIcon={<LinkIcon />}
            onClick={() => setNewLinkOpen(true)}
            disabled={viewMode === 'trash'}
          >
            New link
          </Button>
          <Button
            size="small"
            startIcon={<UploadIcon />}
            onClick={() => uploadRef.current?.click()}
            disabled={viewMode === 'trash'}
          >
            Upload
          </Button>
          <input
            ref={uploadRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => void handleUploadChange(e)}
          />
        </Stack>

        <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

        <Stack
          direction="row"
          spacing={0.75}
          justifyContent={{ xs: 'space-between', md: 'flex-end' }}
        >
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setUploadPanelOpen(true)}
            disabled={uploadEntries.length === 0}
          >
            Operations
            {uploadEntries.some(
              (entry) => entry.status === 'queued' || entry.status === 'uploading'
            )
              ? ` (${
                  uploadEntries.filter(
                    (entry) => entry.status === 'queued' || entry.status === 'uploading'
                  ).length
                })`
              : ''}
          </Button>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={displayMode}
            onChange={(_, value: DisplayMode | null) => {
              if (value) setDisplayMode(value);
            }}
            sx={{
              '& .MuiToggleButton-root': { px: 1, py: 0.4, borderColor: 'divider' },
              '& .Mui-selected': { bgcolor: 'action.selected' },
            }}
          >
            <ToggleButton value="list">
              <Tooltip title="List view">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="tiles">
              <Tooltip title="Tiles view">
                <GridViewIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => void (viewMode === 'trash' ? refetchTrash() : refetch())}
              disabled={isFetching || isTrashFetching}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Body area: navigation + files */}
      <Box
        sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        {/* ── Left sidebar: folder tree ── */}
        <Box
          sx={{
            width: { xs: isNavCollapsed ? 0 : 220, md: isNavCollapsed ? 0 : 240 },
            height: '100%',
            flexShrink: 0,
            borderRight: isNavCollapsed ? 'none' : '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            opacity: isNavCollapsed ? 0 : 1,
            transform: isNavCollapsed ? 'translateX(-10px)' : 'translateX(0)',
            pointerEvents: isNavCollapsed ? 'none' : 'auto',
            transition:
              'width 260ms cubic-bezier(0.2, 0, 0, 1), opacity 220ms ease, transform 260ms cubic-bezier(0.2, 0, 0, 1), border-color 180ms ease',
            willChange: 'width, transform, opacity',
          }}
        >
          <Box sx={{ pt: 0.5 }} />
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <List dense disablePadding>
              <ListItemButton
                dense
                selected={viewMode === 'files' && currentFolderId === undefined}
                onClick={() => {
                  if (viewMode !== 'files') {
                    switchMode('files');
                  } else {
                    setIsFilesNavExpanded((prev) => !prev);
                  }
                }}
                sx={{
                  pl: 0.25,
                  py: 0.5,
                  mx: 0.5,
                  my: 0.25,
                  borderRadius: 1,
                  '&.Mui-selected': { bgcolor: 'transparent' },
                  '&.Mui-selected:hover': { bgcolor: 'action.hover' },
                  transition: 'background-color 140ms ease',
                }}
              >
                <Box sx={{ width: 22, display: 'inline-flex', justifyContent: 'center', mr: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsFilesNavExpanded((prev) => !prev);
                    }}
                    sx={{
                      p: 0.25,
                      transition: 'transform 180ms ease',
                      transform: isFilesNavExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  >
                    <ChevronRight fontSize="small" />
                  </IconButton>
                </Box>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <FilledFolderIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary="Files"
                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                />
              </ListItemButton>
              <Collapse in={isFilesNavExpanded && viewMode === 'files'} timeout={180} unmountOnExit>
                {rootItems.map((item) =>
                  item.isFolder ? (
                    <FolderTreeNode
                      key={item.id}
                      folder={item}
                      depth={1}
                      ancestors={[ROOT]}
                      currentFolderId={currentFolderId}
                      onNavigate={navigate}
                    />
                  ) : (
                    <ListItemButton
                      key={item.id}
                      dense
                      sx={{
                        pl: 4.5,
                        py: 0.5,
                        transition: 'background-color 140ms ease, transform 140ms ease',
                        '&:hover': { transform: 'translateX(2px)' },
                      }}
                      onClick={() => void handleOpenItem(item)}
                    >
                      <Box sx={{ width: 22, mr: 0.5 }} />
                      <ListItemIcon sx={{ minWidth: 24 }}>{itemIcon(item)}</ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          variant: 'body2',
                          noWrap: true,
                          title: item.name,
                        }}
                      />
                    </ListItemButton>
                  )
                )}
              </Collapse>
            </List>
          </Box>
        </Box>

        {/* ── Right main area ── */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {/* Selection toolbar */}
          <Collapse
            in={selectedIds.size > 0}
            timeout={{ enter: 240, exit: 220 }}
            easing={{ enter: 'cubic-bezier(0.2, 0, 0, 1)', exit: 'ease-out' }}
            collapsedSize={0}
            unmountOnExit
          >
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{
                px: 2,
                py: 0.5,
                bgcolor: 'action.selected',
                flexWrap: 'wrap',
                gap: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                {selectionToolbarIds.size} selected
              </Typography>
              {canToolbarDownload && singleToolbarItem && (
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => void handleOpenItem(singleToolbarItem)}
                >
                  Download
                </Button>
              )}
              {canToolbarRename && singleToolbarItem && (
                <Button
                  size="small"
                  startIcon={<RenameIcon />}
                  onClick={() => void handleRename(singleToolbarItem)}
                >
                  Rename
                </Button>
              )}
              <Button
                size="small"
                startIcon={<RolesIcon />}
                onClick={() => openRolesDialog([...selectionToolbarIds])}
                disabled={viewMode === 'trash'}
              >
                Roles
              </Button>
              {canToolbarAccessLink && singleToolbarItem && (
                <Button
                  size="small"
                  startIcon={<AccessLinkIcon />}
                  onClick={() => setAccessLinkFileId(singleToolbarItem.id)}
                >
                  Access link
                </Button>
              )}
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => void handleDeleteSelected()}
              >
                {viewMode === 'trash'
                  ? 'Delete permanently'
                  : selectionToolbarIds.size > 1
                    ? 'Delete selected'
                    : 'Delete'}
              </Button>
              <Button
                size="small"
                startIcon={<MoveIcon />}
                onClick={() => openTransferDialog('move', toolbarSelectedItems)}
                disabled={viewMode === 'trash'}
              >
                Move to
              </Button>
              <Button
                size="small"
                startIcon={<CopyIcon />}
                onClick={() => openTransferDialog('copy', toolbarSelectedItems)}
                disabled={viewMode === 'trash'}
              >
                Copy to
              </Button>
              <Button size="small" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
            </Stack>
          </Collapse>

          {/* File table */}
          <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1.25, md: 2 }, pb: 2, pt: 1 }}>
            {activeError ? (
              <Alert severity="error">Failed to load files.</Alert>
            ) : (
              <Paper
                variant="outlined"
                sx={{ overflowY: 'hidden', overflowX: { xs: 'auto', md: 'hidden' } }}
              >
                {(isFetching || busy) && <LinearProgress sx={{ height: 2 }} />}
                {displayMode === 'list' ? (
                  <Table size="small" stickyHeader sx={{ minWidth: { xs: 720, md: '100%' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={selectedIds.size > 0 && !allSelected}
                            onChange={toggleAll}
                          />
                        </TableCell>
                        <TableCell width={32} sx={{ p: 0.5 }} />
                        <TableCell
                          onClick={() => handleSort('name')}
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          Name {sortIcon('name')}
                        </TableCell>
                        <TableCell
                          sx={{ userSelect: 'none', width: 220, minWidth: 220, maxWidth: 220 }}
                        >
                          Roles
                        </TableCell>
                        <TableCell
                          onClick={() => handleSort('updatedAt')}
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          Date Modified {sortIcon('updatedAt')}
                        </TableCell>
                        <TableCell
                          onClick={() => handleSort('length')}
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          Size {sortIcon('length')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewMode === 'files' && folderData?.haveParentId && (
                        <TableRow
                          hover
                          onClick={() => navigate(path.slice(0, -1))}
                          sx={{ cursor: 'pointer', height: 46 }}
                        >
                          <TableCell padding="checkbox" />
                          <TableCell sx={{ p: 0.5 }}>
                            <FolderIcon fontSize="small" color="warning" />
                          </TableCell>
                          <TableCell sx={{ color: 'primary.main' }}>..</TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell />
                        </TableRow>
                      )}

                      {sortedItems.length === 0 && !busy && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={{ py: 4, color: 'text.disabled' }}
                          >
                            This folder is empty
                          </TableCell>
                        </TableRow>
                      )}

                      {sortedItems.map((item) => (
                        <TableRow
                          key={item.id}
                          hover
                          selected={selectedIds.has(item.id)}
                          sx={{ height: 46 }}
                          onDoubleClick={() => {
                            if (viewMode === 'files') {
                              void handleOpenItem(item);
                            }
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selectedIds.has(item.id)}
                              onChange={() => toggleSelect(item.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>{itemIcon(item)}</TableCell>
                          <TableCell
                            sx={{ cursor: 'pointer', color: 'primary.main' }}
                            onClick={() => {
                              if (item.isFolder) void handleOpenItem(item);
                            }}
                          >
                            {item.name}
                          </TableCell>
                          <TableCell sx={{ width: 220, minWidth: 220, maxWidth: 220 }}>
                            <Tooltip
                              title={formatRoles(item.roles)}
                              placement="top"
                              disableInteractive
                              disableHoverListener={isMobile || item.roles.length === 0}
                              disableFocusListener={isMobile || item.roles.length === 0}
                              disableTouchListener
                              open={
                                isMobile && item.roles.length > 0
                                  ? mobileRolesTooltipId === item.id
                                  : undefined
                              }
                              onClose={() => {
                                if (!isMobile) {
                                  setMobileRolesTooltipId(null);
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  maxWidth: 220,
                                  width: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'default',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (item.roles.length === 0) return;
                                  handleRolesClick(item.id);
                                }}
                              >
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {formatRoles(item.roles)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : ''}
                          </TableCell>
                          <TableCell>
                            {item.isUrl ? '—' : formatFileSize(item, { emptyFileValue: '—' })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Box sx={{ p: 1 }}>
                    <Stack direction="row" alignItems="center" sx={{ px: 0.5, mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={selectedIds.size > 0 && !allSelected}
                            onChange={toggleAll}
                          />
                        }
                        label="Select all"
                      />
                    </Stack>

                    {sortedItems.length === 0 && !busy ? (
                      <Box sx={{ py: 4, color: 'text.disabled', textAlign: 'center' }}>
                        This folder is empty
                      </Box>
                    ) : (
                      <Grid container spacing={1.25}>
                        {viewMode === 'files' && folderData?.haveParentId && (
                          <Grid size={{ xs: 6, sm: 6, md: 4, lg: 2 }}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                minHeight: 126,
                                height: '100%',
                                borderRadius: 1.5,
                                cursor: 'pointer',
                                borderColor: 'divider',
                                transition: 'all 140ms ease',
                                '&:hover': { boxShadow: 1, borderColor: 'primary.main' },
                              }}
                              onClick={() => navigate(path.slice(0, -1))}
                            >
                              <Stack spacing={0.75} sx={{ height: '100%' }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Box
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: 1,
                                      display: 'grid',
                                      placeItems: 'center',
                                      bgcolor: 'action.hover',
                                    }}
                                  >
                                    <FolderIcon fontSize="small" color="warning" />
                                  </Box>
                                  <Typography variant="body2" color="primary.main" fontWeight={600}>
                                    ..
                                  </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  Parent folder
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                              </Stack>
                            </Paper>
                          </Grid>
                        )}

                        {sortedItems.map((item) => (
                          <Grid key={item.id} size={{ xs: 6, sm: 6, md: 4, lg: 2 }}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                minHeight: 126,
                                height: '100%',
                                borderRadius: 1.5,
                                borderColor: selectedIds.has(item.id) ? 'primary.light' : 'divider',
                                bgcolor: selectedIds.has(item.id)
                                  ? 'action.selected'
                                  : 'background.paper',
                                transition: 'all 140ms ease',
                                '&:hover': { boxShadow: 1, borderColor: 'primary.main' },
                              }}
                            >
                              <Stack spacing={0.75} sx={{ height: '100%' }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ minWidth: 0 }}
                                  >
                                    <Box
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: 1,
                                        display: 'grid',
                                        placeItems: 'center',
                                        bgcolor: 'action.hover',
                                      }}
                                    >
                                      {itemIcon(item)}
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        cursor: item.isFolder ? 'pointer' : 'default',
                                        color: item.isFolder ? 'primary.main' : 'text.primary',
                                        fontWeight: item.isFolder ? 600 : 400,
                                      }}
                                      onClick={() => {
                                        if (item.isFolder) void handleOpenItem(item);
                                      }}
                                      title={item.name}
                                    >
                                      {item.name}
                                    </Typography>
                                  </Stack>
                                  <Checkbox
                                    size="small"
                                    checked={selectedIds.has(item.id)}
                                    onChange={() => toggleSelect(item.id)}
                                  />
                                </Stack>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    lineHeight: 1.2,
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : ''}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    lineHeight: 1.2,
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {item.isUrl ? '—' : formatFileSize(item, { emptyFileValue: '—' })}
                                </Typography>
                                <Tooltip
                                  title={formatRoles(item.roles)}
                                  placement="top"
                                  disableInteractive
                                  disableHoverListener={isMobile || item.roles.length === 0}
                                  disableFocusListener={isMobile || item.roles.length === 0}
                                  disableTouchListener
                                  open={
                                    isMobile && item.roles.length > 0
                                      ? mobileRolesTooltipId === item.id
                                      : undefined
                                  }
                                  onClose={() => {
                                    if (!isMobile) {
                                      setMobileRolesTooltipId(null);
                                    }
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      lineHeight: 1.2,
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      cursor: 'default',
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (item.roles.length === 0) return;
                                      handleRolesClick(item.id);
                                    }}
                                  >
                                    {formatRoles(item.roles)}
                                  </Typography>
                                </Tooltip>

                                <Box sx={{ flex: 1 }} />
                              </Stack>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        </Box>
      </Box>

      <UploadProgressPanel
        entries={uploadEntries}
        open={uploadPanelOpen}
        onClose={() => setUploadPanelOpen(false)}
        onCancel={cancelUpload}
        onRetry={retryUpload}
      />

      {/* ── Dialogs ── */}
      <CreateLinkDialog
        open={newLinkOpen}
        onClose={() => setNewLinkOpen(false)}
        onSubmit={(name, url) => void handleNewLink(name, url)}
      />
      <CreateFolderDialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        onSubmit={(name) => void handleCreateFolder(name)}
      />

      {renameTarget && (
        <RenameDialog
          open={!!renameTarget}
          currentName={renameTarget.name}
          lockExtension={!renameTarget.isFolder && !renameTarget.isUrl}
          onClose={() => setRenameTarget(null)}
          onSubmit={(newName) => void handleRenameSubmit(newName)}
        />
      )}

      <ConfirmDialog
        open={deleteTargets.length > 0}
        title={deleteDialogText.title}
        description={deleteDialogText.description}
        confirmLabel={deleteDialogText.confirmLabel}
        confirmColor="error"
        processing={viewMode === 'trash' && deleteDialogPhase === 'processing'}
        errorMessage={viewMode === 'trash' ? deleteDialogError : null}
        disableClose={viewMode === 'trash' && deleteDialogPhase === 'processing'}
        onClose={handleDeleteClose}
        onConfirm={() => void handleDeleteConfirm()}
      />

      {transferMode && transferTargets.length > 0 && (
        <TransferItemsDialog
          open={!!transferMode}
          mode={transferMode}
          currentPath={path}
          sourceItems={transferTargets}
          onClose={handleTransferClose}
          onConfirm={(destinationPath) => void handleTransferConfirm(destinationPath)}
        />
      )}

      {rolesOpen && (
        <RolesDialog
          open={rolesOpen}
          filesKeys={[...selectedIds]}
          initialSelectedRoles={initialSelectedRoles}
          onClose={() => {
            setRolesOpen(false);
            setInitialSelectedRoles([]);
          }}
          onSubmit={(roles) => void handleRolesSubmit(roles)}
        />
      )}

      {accessLinkFileId && (
        <AccessLinkDialog
          open={!!accessLinkFileId}
          fileId={accessLinkFileId}
          onClose={() => setAccessLinkFileId(null)}
        />
      )}

      <Snackbar
        open={!!errorSnackbar}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorSnackbar(null)}
          sx={{ width: '100%' }}
        >
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
};
